export interface ModelConfig {
  value: string;
  label: string;
  defaultDisplayName: string;
  supportsFileUpload?: boolean;
  maxFileSize?: number; // in MB
  supportedFileTypes?: string[];
  supportsAdvancedRetrieval?: boolean; // Whether model can handle complex agentic retrieval
  contextWindow?: number; // Max tokens the model can handle
  threatDetectionReliability?: 'excellent' | 'good' | 'fair' | 'poor'; // How well it detects prompt injections/jailbreaks
  abuseDetectionReliability?: 'excellent' | 'good' | 'fair' | 'poor'; // How well it detects harmful content
  supportsReasoning?: boolean; // Whether model supports explicit reasoning formats
  reasoningEffortOptions?: ('none' | 'default' | 'low' | 'medium' | 'high')[]; // Available reasoning effort levels
  defaultReasoningFormat?: 'parsed' | 'raw' | 'hidden'; // Default reasoning format
  useMaxCompletionTokens?: boolean; // Whether to use max_completion_tokens instead of max_tokens
}

export interface ProviderModels {
  OPENAI: ModelConfig[];
  ANTHROPIC: ModelConfig[];
  GROQ: ModelConfig[];
  VECTORIZE: ModelConfig[];
}

export const MODEL_CONFIGS: ProviderModels = {
  OPENAI: [
    {
      value: 'gpt-5',
      label: 'GPT-5',
      defaultDisplayName: 'Advanced reasoning, 400K context',
      supportsFileUpload: true,
      maxFileSize: 20,
      supportedFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      supportsAdvancedRetrieval: true,
      supportsReasoning: true,
      contextWindow: 400000,
      threatDetectionReliability: 'excellent',
      abuseDetectionReliability: 'excellent',
      useMaxCompletionTokens: true,
    },
    {
      value: 'gpt-5-mini',
      label: 'GPT-5 mini',
      defaultDisplayName: 'Balanced reasoning, 400K context',
      supportsFileUpload: true,
      maxFileSize: 20,
      supportedFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      supportsAdvancedRetrieval: true,
      supportsReasoning: true,
      contextWindow: 400000,
      threatDetectionReliability: 'good',
      abuseDetectionReliability: 'good',
      useMaxCompletionTokens: true,
    },
    {
      value: 'gpt-5-nano',
      label: 'GPT-5 nano',
      defaultDisplayName: 'Fast reasoning, 400K context',
      supportsAdvancedRetrieval: false,
      supportsReasoning: true,
      contextWindow: 400000,
      threatDetectionReliability: 'fair',
      abuseDetectionReliability: 'fair',
      useMaxCompletionTokens: true,
    },
    {
      value: 'gpt-4o',
      label: 'GPT-4o',
      defaultDisplayName: 'Balanced speed and intelligence',
      supportsFileUpload: true,
      maxFileSize: 20,
      supportedFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      supportsAdvancedRetrieval: true,
      contextWindow: 128000,
      threatDetectionReliability: 'excellent',
      abuseDetectionReliability: 'excellent',
    },
    {
      value: 'gpt-4o-mini',
      label: 'GPT-4o mini',
      defaultDisplayName: 'Fast for simple queries',
      supportsFileUpload: true,
      maxFileSize: 20,
      supportedFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      supportsAdvancedRetrieval: true,
      contextWindow: 128000,
      threatDetectionReliability: 'good',
      abuseDetectionReliability: 'good',
    },
    {
      value: 'gpt-4.1',
      label: 'GPT-4.1',
      defaultDisplayName: 'Best for complex analysis',
      supportsFileUpload: true,
      maxFileSize: 20,
      supportedFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      supportsAdvancedRetrieval: true,
      contextWindow: 1000000,
      threatDetectionReliability: 'excellent',
      abuseDetectionReliability: 'excellent',
    },
    {
      value: 'gpt-4.1-mini',
      label: 'GPT-4.1 mini',
      defaultDisplayName: 'Good performance, lower cost',
      supportsFileUpload: true,
      maxFileSize: 20,
      supportedFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      supportsAdvancedRetrieval: true,
      contextWindow: 1000000,
      threatDetectionReliability: 'good',
      abuseDetectionReliability: 'good',
    },
    {
      value: 'gpt-4.1-nano',
      label: 'GPT-4.1 nano',
      defaultDisplayName: 'Ultra-fast basic questions',
      supportsAdvancedRetrieval: false,
      contextWindow: 1000000,
      threatDetectionReliability: 'fair',
      abuseDetectionReliability: 'fair',
    },
    {
      value: 'o3',
      label: 'o3',
      defaultDisplayName: 'Advanced math, coding, logic',
      supportsAdvancedRetrieval: true,
      contextWindow: 200000,
      threatDetectionReliability: 'good',
      abuseDetectionReliability: 'good',
      supportsReasoning: true,
      reasoningEffortOptions: ['low', 'medium', 'high'],
      defaultReasoningFormat: 'parsed',
      useMaxCompletionTokens: true,
    },
    {
      value: 'o3-pro',
      label: 'o3-pro',
      defaultDisplayName: 'Maximum reasoning power',
      supportsAdvancedRetrieval: true,
      contextWindow: 200000,
      threatDetectionReliability: 'excellent',
      abuseDetectionReliability: 'excellent',
      supportsReasoning: true,
      reasoningEffortOptions: ['low', 'medium', 'high'],
      defaultReasoningFormat: 'parsed',
      useMaxCompletionTokens: true,
    },
    {
      value: 'o3-mini',
      label: 'o3-mini',
      defaultDisplayName: 'Quick moderate complexity reasoning',
      supportsAdvancedRetrieval: false,
      contextWindow: 200000,
      threatDetectionReliability: 'good',
      abuseDetectionReliability: 'good',
      supportsReasoning: true,
      reasoningEffortOptions: ['low', 'medium', 'high'],
      defaultReasoningFormat: 'parsed',
      useMaxCompletionTokens: true,
    },
  ],
  ANTHROPIC: [
    {
      value: 'claude-opus-4-1',
      label: 'Claude Opus 4.1',
      defaultDisplayName: 'Most powerful model for complex work',
      supportsFileUpload: true,
      maxFileSize: 10,
      supportedFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'],
      supportsAdvancedRetrieval: true,
      contextWindow: 200000,
      threatDetectionReliability: 'excellent',
      abuseDetectionReliability: 'excellent',
    },
    {
      value: 'claude-opus-4-0',
      label: 'Claude Opus 4',
      defaultDisplayName: 'Exceptional for complex tasks',
      supportsFileUpload: true,
      maxFileSize: 10,
      supportedFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'],
      supportsAdvancedRetrieval: true,
      contextWindow: 200000,
      threatDetectionReliability: 'excellent',
      abuseDetectionReliability: 'excellent',
    },
    {
      value: 'claude-sonnet-4-0',
      label: 'Claude Sonnet 4',
      defaultDisplayName: 'Great for daily writing, analysis',
      supportsFileUpload: true,
      maxFileSize: 10,
      supportedFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'],
      supportsAdvancedRetrieval: true,
      contextWindow: 200000,
      threatDetectionReliability: 'excellent',
      abuseDetectionReliability: 'excellent',
    },
    {
      value: 'claude-3-7-sonnet-latest',
      label: 'Claude 3.7 Sonnet',
      defaultDisplayName: 'Advanced tasks, smart and flexible',
      supportsFileUpload: true,
      maxFileSize: 10,
      supportedFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'],
      supportsAdvancedRetrieval: true,
      contextWindow: 200000,
      threatDetectionReliability: 'excellent',
      abuseDetectionReliability: 'excellent',
    },
    {
      value: 'claude-3-5-sonnet-latest',
      label: 'Claude 3.5 Sonnet',
      defaultDisplayName: 'Best overall, reports and coding',
      supportsFileUpload: true,
      maxFileSize: 10,
      supportedFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'],
      supportsAdvancedRetrieval: true,
      contextWindow: 200000,
      threatDetectionReliability: 'excellent',
      abuseDetectionReliability: 'excellent',
    },
    {
      value: 'claude-3-5-haiku-latest',
      label: 'Claude 3.5 Haiku',
      defaultDisplayName: 'Quick, simple answers, instant help',
      supportsFileUpload: true,
      maxFileSize: 10,
      supportedFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'],
      supportsAdvancedRetrieval: false,
      contextWindow: 200000,
      threatDetectionReliability: 'good',
      abuseDetectionReliability: 'good',
    },
    {
      value: 'claude-3-sonnet-20240229',
      label: 'Claude 3 Sonnet',
      defaultDisplayName: 'Reliable for most general tasks',
      supportsFileUpload: true,
      maxFileSize: 10,
      supportedFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      supportsAdvancedRetrieval: true,
      contextWindow: 200000,
      threatDetectionReliability: 'good',
      abuseDetectionReliability: 'good',
    },
    {
      value: 'claude-3-opus-20240229',
      label: 'Claude 3 Opus',
      defaultDisplayName: 'Great for research, deep questions',
      supportsFileUpload: true,
      maxFileSize: 10,
      supportedFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      supportsAdvancedRetrieval: true,
      contextWindow: 200000,
      threatDetectionReliability: 'excellent',
      abuseDetectionReliability: 'excellent',
    },
    {
      value: 'claude-3-haiku-20240307',
      label: 'Claude 3 Haiku',
      defaultDisplayName: 'Fastest for basic, quick info',
      supportsFileUpload: true,
      maxFileSize: 10,
      supportedFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      supportsAdvancedRetrieval: false,
      contextWindow: 200000,
      threatDetectionReliability: 'fair',
      abuseDetectionReliability: 'fair',
    },
  ],
  GROQ: [
    {
      value: 'llama-3.1-8b-instant',
      label: 'Llama 3.1 8B Instant',
      defaultDisplayName: 'Good for basic chat.',
      supportsAdvancedRetrieval: false,
      contextWindow: 131072,
      threatDetectionReliability: 'poor',
      abuseDetectionReliability: 'poor',
    },
    {
      value: 'llama-3.3-70b-versatile',
      label: 'Llama 3.3 70B',
      defaultDisplayName: 'Extremely fast open-source model for instant responses',
      supportsAdvancedRetrieval: false,
      contextWindow: 128000,
      threatDetectionReliability: 'fair',
      abuseDetectionReliability: 'fair',
    },
    {
      value: 'qwen/qwen3-32b',
      label: 'Qwen 3 32B',
      defaultDisplayName: 'Advanced reasoning, multilingual, creative tasks',
      supportsAdvancedRetrieval: true,
      contextWindow: 32768,
      threatDetectionReliability: 'good',
      abuseDetectionReliability: 'good',
      supportsReasoning: true,
      reasoningEffortOptions: ['none', 'default'],
      defaultReasoningFormat: 'parsed',
    },
    {
      value: 'meta-llama/llama-4-maverick-17b-128e-instruct',
      label: 'Llama 4 Maverick 17B 128E',
      defaultDisplayName: 'Exceptional performance across benchmarks',
      supportsAdvancedRetrieval: true,
      supportsFileUpload: true,
      maxFileSize: 20,
      contextWindow: 128000,
      threatDetectionReliability: 'fair',
      abuseDetectionReliability: 'fair',
    },
    {
      value: 'openai/gpt-oss-120b',
      label: 'GPT OSS 120B',
      defaultDisplayName: "OpenAI's flagship MoE model w/120B params",
      supportsAdvancedRetrieval: true,
      contextWindow: 128000,
      threatDetectionReliability: 'good',
      abuseDetectionReliability: 'good',
      supportsReasoning: true,
      reasoningEffortOptions: ['low', 'medium', 'high'],
      defaultReasoningFormat: 'parsed',
    },
    {
      value: 'openai/gpt-oss-20b',
      label: 'GPT OSS 20B',
      defaultDisplayName: "OpenAI's flagship MoE model w/20B params",
      supportsAdvancedRetrieval: true,
      contextWindow: 128000,
      threatDetectionReliability: 'fair',
      abuseDetectionReliability: 'fair',
      supportsReasoning: true,
      reasoningEffortOptions: ['low', 'medium', 'high'],
      defaultReasoningFormat: 'parsed',
    },
  ],
  VECTORIZE: [
    {
      value: 'openai/gpt-oss-120b',
      label: 'OpenAI GPT OSS 120B',
      defaultDisplayName: 'Extremely capable open-source model.',
      supportsAdvancedRetrieval: true,
      contextWindow: 128000,
      threatDetectionReliability: 'good',
      abuseDetectionReliability: 'good',
      supportsReasoning: true,
      reasoningEffortOptions: ['low', 'medium', 'high'],
      defaultReasoningFormat: 'parsed',
    },
    {
      value: 'qwen/qwen3-32b',
      label: 'Qwen3-32B',
      defaultDisplayName: 'Powerful multilingual, reasoning model.',
      supportsAdvancedRetrieval: true,
      contextWindow: 32768,
      threatDetectionReliability: 'good',
      abuseDetectionReliability: 'good',
      supportsReasoning: true,
      reasoningEffortOptions: ['none', 'default'],
      defaultReasoningFormat: 'parsed',
    },
  ],
};

// Helper function to get model config
export function getModelConfig(provider: string, modelValue: string): ModelConfig | undefined {
  const providerModels = MODEL_CONFIGS[provider as keyof ProviderModels];
  if (!providerModels) return undefined;

  return providerModels.find((model) => model.value === modelValue);
}

// Helper function to get display name with i18n fallback
export function getModelDisplayName(provider: string, modelValue: string, t?: (key: string) => string): string {
  const modelConfig = getModelConfig(provider, modelValue);
  if (!modelConfig) return modelValue;

  if (t) {
    // Try to get translated description
    const translationKey = `models.${provider.toLowerCase()}.${modelValue.replace(/\./g, '_').replace(/-/g, '_')}.description`;
    const translated = t(translationKey);
    // If translation exists and is different from key, use it
    if (translated && translated !== translationKey) {
      return translated;
    }
  }

  // Fallback to default display name
  return modelConfig.defaultDisplayName;
}
